import type { QuoteLike } from "@/Shared/Quotes/Types/QuoteLikeType";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import QuotesDeliveryColumn from "@/Shared/Quotes/CustomComponents/QuotesDeliveryColumn";
import QuotesPickupColumn from "@/Shared/Quotes/CustomComponents/QuotesPickupColumn";
import QuotesProgress from "@/Shared/Quotes/CustomComponents/QuotesProgress";
import QuotesTransitColumn from "@/Shared/Quotes/CustomComponents/QuotesTransitColumn";
import { isDomesticCanada } from "@/Helpers/isDomesticCanada";
import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { StateOfGoods } from "@freightsimple/generated-dashboard-openapi-client/models/StateOfGoods";
import type { TrackingLine } from "@freightsimple/generated-dashboard-openapi-client/models/TrackingLine";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import type { EquipmentType } from "@freightsimple/generated-dashboard-openapi-client/models/EquipmentType";
import moment from "moment";
import ShipmentSection from "@/Shared/Shipment/CustomComponents/ShipmentSection";
import { ShipmentMostRecentTrackingLine } from "@/Shared/Shipment/CustomComponents/ShipmentMostRecentTrackingLine";
import { QuotesColumnHeader } from "@/Shared/Quotes/CustomComponents/QuotesColumnHeader";
import { QuotesPrimaryText } from "@/Shared/Quotes/CustomComponents/QuotesPrimaryText";

// Like Quote object, but with fewer required fields

interface DetailsSectionProps {
  pickupDate: moment.Moment;
  pickupDeadline?: string | undefined;
  deliveryHours: string | undefined;
  pickupLocation: Location | AddLocationLocationType;
  deliveryLocation: Location | AddLocationLocationType;
  deliveryDeadline: string | undefined;
  appointmentDate: string | undefined;
  pickupContact: Contact | undefined;
  deliveryContact: Contact | undefined;
  lineItems: Array<LineItem | AddLineItemType>;
  quote: QuoteLike; // Might not have all the info when rendering for tracking
  shipmentState: ShipmentState;
  actualDeliveryDate: string | undefined;
  actualDeliveryTime: string | undefined;
  proNumber: string | undefined;
  billOfLadingNumber?: string | undefined;
  expectedDeliveryDate: string | undefined;
  latestExpectedDeliveryDate: string | undefined;
  pickupReferenceNumber: string | undefined;
  deliveryReferenceNumber: string | undefined;
  deliveryAmazonReferenceId: string | undefined;
  deliveryAmazonShipmentId: string | undefined;
  pickupBoothNumber: string | undefined;
  deliveryBoothNumber: string | undefined;
  broker: Broker | undefined;
  mostRecentTrackingLine: TrackingLine | undefined;
  addInsuranceToShipment: boolean;
  insuranceAmount: number;
  insuranceCurrency: Currency;
  stateOfGoods: StateOfGoods;
  insuranceCommodityGroup: string | undefined;
  equipmentType: EquipmentType | undefined;
  linearFeet: number | undefined;
  exclusiveUse: boolean;
  tarpRequired: boolean;
  pickupLocationHasDock?: LoadingCapabilitiesAvailability | undefined;
  pickupLocationHasForklift?: LoadingCapabilitiesAvailability | undefined;
  deliveryLocationHasDock?: LoadingCapabilitiesAvailability | undefined;
  deliveryLocationHasForklift?: LoadingCapabilitiesAvailability | undefined;
  onChangePickupDate: () => Promise<void>;
  onChangeBroker: () => Promise<void>;
  onModifyPickupAddress: () => Promise<void>;
  onModifyDeliveryAddress: () => Promise<void>;
  onModifyPickupContact: () => Promise<void>;
  onModifyDeliveryContact: () => Promise<void>;
  onModifyPickupReferenceAndNotes: () => Promise<void>;
  onModifyDeliveryReferenceAndNotes: () => Promise<void>;
  /** Undefined when the delivery is by appointment, which hides the link. */
  onChangeDeliveryHours: (() => Promise<void>) | undefined;
}
function ViewShipmentsDetailsSection(props: DetailsSectionProps) {
  const {
    pickupDate,
    deliveryHours,
    pickupLocation,
    pickupBoothNumber,
    deliveryBoothNumber,
    deliveryLocation,
    pickupContact,
    deliveryContact,
    deliveryDeadline,
    appointmentDate,
    lineItems,
    quote,
    shipmentState,
    actualDeliveryDate,
    actualDeliveryTime,
    expectedDeliveryDate,
    latestExpectedDeliveryDate,
    proNumber,
    billOfLadingNumber,
    pickupReferenceNumber,
    deliveryReferenceNumber,
    deliveryAmazonReferenceId,
    deliveryAmazonShipmentId,
    broker,
    onChangePickupDate,
    mostRecentTrackingLine,
    addInsuranceToShipment,
    insuranceAmount,
    insuranceCurrency,
    insuranceCommodityGroup,
    stateOfGoods,
    equipmentType,
    exclusiveUse,
    tarpRequired,
    linearFeet,
    pickupDeadline,
  } = props;

  function checkPermissable(
    f: () => Promise<void>,
    allowIfInTransit: boolean,
  ): (() => Promise<void>) | undefined {
    if (allowIfInTransit) {
      return shipmentState === ShipmentState.Cancelled ||
        shipmentState === ShipmentState.Delivered ||
        shipmentState === ShipmentState.Lost
        ? undefined
        : f;
    } else {
      return shipmentState === ShipmentState.InTransit ||
        shipmentState === ShipmentState.Cancelled ||
        shipmentState === ShipmentState.Delivered ||
        shipmentState === ShipmentState.Lost
        ? undefined
        : f;
    }
  }

  const onChangeBroker = checkPermissable(props.onChangeBroker, true);
  const onModifyPickupContact = checkPermissable(
    props.onModifyPickupContact,
    false,
  );

  const onModifyPickupAddress = checkPermissable(
    props.onModifyPickupAddress,
    false,
  );

  const onModifyPickupReferenceAndNotes = checkPermissable(
    props.onModifyPickupReferenceAndNotes,
    false,
  );

  const onModifyDeliveryContact = checkPermissable(
    props.onModifyDeliveryContact,
    true,
  );

  const onModifyDeliveryAddress = checkPermissable(
    props.onModifyDeliveryAddress,
    true,
  );

  const onModifyDeliveryReferenceAndNotes = checkPermissable(
    props.onModifyDeliveryReferenceAndNotes,
    true,
  );

  const onChangeDeliveryHours =
    props.onChangeDeliveryHours &&
    checkPermissable(props.onChangeDeliveryHours, true);

  return (
    <ShipmentSection title="Details" id="details-section">
      {quote?.notes && (
        <>
          <QuotesPrimaryText bold>Notes</QuotesPrimaryText>
          <QuotesColumnHeader>{quote.notes}</QuotesColumnHeader>
          <div className="h-[48px]" />
        </>
      )}
      <ShipmentMostRecentTrackingLine
        mostRecentTrackingLine={mostRecentTrackingLine}
      />
      <QuotesProgress shipmentState={shipmentState} />
      <div className="h-[16px]" />
      <div className="flex w-full flex-row items-start">
        <QuotesPickupColumn
          pickupDeadline={pickupDeadline}
          lineItems={lineItems}
          pickupDate={pickupDate}
          pickupLocation={pickupLocation}
          pickupContact={pickupContact}
          shipmentState={shipmentState}
          equipmentType={equipmentType}
          exclusiveUseNeeded={exclusiveUse}
          tarpRequired={tarpRequired}
          linearFeet={linearFeet}
          quote={quote}
          pickupReferenceNumber={pickupReferenceNumber}
          onChangePickupDate={onChangePickupDate}
          isDomesticCanada={isDomesticCanada(pickupLocation, deliveryLocation)}
          onModifyPickupAddress={onModifyPickupAddress}
          onModifyPickupContact={onModifyPickupContact}
          onModifyPickupReferenceAndNotes={onModifyPickupReferenceAndNotes}
          showNotesSection={true}
          pickupBoothNumber={pickupBoothNumber}
          pickupLocationHasDock={props.pickupLocationHasDock}
          pickupLocationHasForklift={props.pickupLocationHasForklift}
        />
        <QuotesTransitColumn
          quote={quote}
          shipmentState={shipmentState}
          proNumber={proNumber}
          billOfLadingNumber={billOfLadingNumber}
          broker={broker}
          onChangeBroker={onChangeBroker}
          addInsuranceToShipment={addInsuranceToShipment}
          insuranceAmount={insuranceAmount}
          insuranceCurrency={insuranceCurrency}
          stateOfGoods={stateOfGoods}
          insuranceCommodityGroup={insuranceCommodityGroup}
        />
        <QuotesDeliveryColumn
          quote={quote}
          deliveryHours={deliveryHours}
          appointmentDate={appointmentDate}
          deliveryLocation={deliveryLocation}
          deliveryContact={deliveryContact}
          deliveryDeadline={deliveryDeadline}
          shipmentState={shipmentState}
          actualDeliveryDate={actualDeliveryDate}
          actualDeliveryTime={actualDeliveryTime}
          expectedDeliveryDate={expectedDeliveryDate}
          latestExpectedDeliveryDate={latestExpectedDeliveryDate}
          deliveryReferenceNumber={deliveryReferenceNumber}
          deliveryAmazonReferenceId={deliveryAmazonReferenceId}
          deliveryAmazonShipmentId={deliveryAmazonShipmentId}
          onModifyDeliveryAddress={onModifyDeliveryAddress}
          onModifyDeliveryContact={onModifyDeliveryContact}
          onModifyDeliveryReferenceAndNotes={onModifyDeliveryReferenceAndNotes}
          onChangeDeliveryHours={onChangeDeliveryHours}
          showNotesSection={true}
          deliveryBoothNumber={deliveryBoothNumber}
          deliveryLocationHasDock={props.deliveryLocationHasDock}
          deliveryLocationHasForklift={props.deliveryLocationHasForklift}
        />
      </div>
    </ShipmentSection>
  );
}
export default ViewShipmentsDetailsSection;
